import React from "react";
import { Input, message } from "antd";
import type { InputProps } from "antd";

interface TextInputProps extends Omit<InputProps, "value" | "onChange" | "maxLength"> {
  value: string;
  onChange: (value: string) => void;
  /** When true, rejects digits — blocks digit keystrokes outright and strips
   *  any digits out of pasted/dropped content, so the value can never end up
   *  numeric either way, showing a `message.warning` either way (mirrors
   *  `maxLength`'s own warning below). Off by default (plain controlled text
   *  `Input`). Use for a field like the Add/Edit Item modal's "Amount (Text)",
   *  which is meant for values like "Included"/"Rate Only", not a numeric
   *  amount (that's what the Number amount type + rate/qty fields are for). */
  textOnly?: boolean;
  /** Maximum character count — enforced by the underlying antd `Input` (both
   *  typing and paste), with a `message.warning` shown once the limit is hit
   *  (mirrors `NumberInput.tsx`'s own max-digits warning). No limit when
   *  omitted. */
  maxLength?: number;
}

const MAX_LENGTH_MESSAGE_KEY = "text-input-max-length";
const DIGITS_NOT_ALLOWED_MESSAGE_KEY = "text-input-digits-not-allowed";

const warnMaxLength = (limit: number) => {
  message.warning({
    content: `Maximum ${limit} characters allowed.`,
    key: MAX_LENGTH_MESSAGE_KEY,
  });
};

const warnDigitsNotAllowed = () => {
  message.warning({
    content: "This is a text field. Numbers are not allowed.",
    key: DIGITS_NOT_ALLOWED_MESSAGE_KEY,
  });
};

/** A controlled text `Input` taking/returning a plain string value, with an
 *  optional `textOnly` mode that rejects digits and an optional `maxLength`
 *  that warns once hit. `maxLength` is enforced in JS (truncating in
 *  `onChange`), not just via the native `maxLength` attribute — antd's
 *  `Input` doesn't reliably block typing past it on its own once a
 *  controlled `value` is involved, so the pre-emptive `onKeyDown` block below
 *  is backed by a hard clamp here too. */
const TextInput: React.FC<TextInputProps> = ({ value, onChange, onKeyDown, onPaste, textOnly = false, maxLength, ...rest }) => (
  <Input
    {...rest}
    value={value}
    maxLength={maxLength}
    onChange={(e) => {
      let next = e.target.value;
      if (textOnly && /[0-9]/.test(next)) {
        next = next.replace(/[0-9]/g, "");
        warnDigitsNotAllowed();
      }
      if (maxLength != null && next.length > maxLength) {
        next = next.slice(0, maxLength);
        warnMaxLength(maxLength);
      }
      onChange(next);
    }}
    onKeyDown={(e) => {
      if (textOnly && e.key.length === 1 && /[0-9]/.test(e.key) && !e.ctrlKey && !e.metaKey) {
        e.preventDefault();
        warnDigitsNotAllowed();
      } else if (
        maxLength != null &&
        value.length >= maxLength &&
        e.key.length === 1 &&
        !e.ctrlKey &&
        !e.metaKey
      ) {
        e.preventDefault();
        warnMaxLength(maxLength);
      }
      onKeyDown?.(e);
    }}
    onPaste={(e) => {
      if (maxLength != null) {
        const pasted = e.clipboardData.getData("text");
        if (value.length + pasted.length > maxLength) {
          warnMaxLength(maxLength);
        }
      }
      onPaste?.(e);
    }}
  />
);

export default TextInput;
